import Moment from "moment";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import moment from "moment";
import axios from "axios";
import momentTimezone from "moment-timezone";
import {
  DEFAULT_BANNER_IMAGE,
  DEFAULT_PROFILE_IMAGE,
} from "./brandAssets";
import { getApiBaseUrl, getMerchBaseUrl, isDanzfloSiteHost } from "./apiBase";

function getUserData() {
  if (localStorage.getItem("userData")) {
    return JSON.parse(localStorage.getItem("userData"));
  }
  return {
    message: "you are not logged in",
    token: false,
    userData: [],
  };
}

function getGuardData() {
  if (localStorage.getItem("guardData")) {
    return JSON.parse(localStorage.getItem("guardData"));
  }
  return {
    message: "you are not logged in",
    token: false,
    guardData: [],
  };
}

/**
 * Master SSO token from the query string.
 * Accepts token, Token, and authId. Spaces are restored to "+" because
 * base64 uses "+" and query parsers treat "+" as a space.
 */
function readQueryLoginToken(search = "") {
  const params = new URLSearchParams(typeof search === "string" ? search : "");
  const raw =
    params.get("token") || params.get("Token") || params.get("authId") || "";
  return String(raw).replace(/ /g, "+").trim();
}

/**
 * Path for the public auth page when the current URL already carries an SSO token.
 * Other query params (userName, referralId, …) are kept. Returns "" when no token.
 */
function buildQueryTokenLoginPath(pathname = "/", search = "", hash = "") {
  const token = readQueryLoginToken(search);
  if (!token) return "";

  const params = new URLSearchParams(search || "");
  params.delete("token");
  params.delete("Token");
  params.delete("authId");

  const authParams = new URLSearchParams();
  authParams.set("token", token);

  const userName = params.get("userName");
  if (userName) authParams.set("userName", userName);
  params.delete("userName");

  const rest = params.toString();
  const path = pathname || "/";
  const returnTo = `${path}${rest ? `?${rest}` : ""}${hash || ""}`;
  if (path !== "/" || rest || hash) {
    authParams.set("redirect", returnTo);
  }

  return `/auth?${authParams.toString()}`;
}

/** Set before clearing the session so the auth gate does not add ?redirect=. */
let platformLogoutStarted = false;

function beginPlatformLogout() {
  platformLogoutStarted = true;
}

function isPlatformLogout() {
  return platformLogoutStarted;
}

/** Login only. Logout must not send a return URL. */
function platformLogoutUrl() {
  return `${getMerchBaseUrl()}/login?isLogout=true`;
}

/**
 * Unauthorized redirect — same rules for guard vs event sessions.
 * Guard-only /guard routes → /guard/login; otherwise merch login.
 */
function redirectUnauthorized({ forceGuard = false } = {}) {
  if (typeof window === "undefined") return;

  const path = String(window.location.pathname || "");
  const onGuardRoute = /\/guard(\/|$)/.test(path);
  const guardOnly =
    Boolean(getGuardData()?.token) && !Boolean(getUserData()?.token);

  if (forceGuard || onGuardRoute || guardOnly) {
    const base = String(process.env.REACT_APP_BASE_URL || "/event").replace(
      /\/$/,
      ""
    );
    window.location.assign(`${base}/guard/login`);
    return;
  }

  // Same destination as Auth SSO bounce / PrivateRoute.
  window.location.assign(`${getMerchBaseUrl()}/login?platform=event`);
}

function dateFormatter(reqDate, ...restParams) {
  if (reqDate === undefined || reqDate === null || reqDate === "") {
    return "";
  }
  let formatType = restParams ? restParams[0] : "date";
  let format =
    formatType === "time"
      ? restParams[1]
      : restParams[1]
      ? restParams[1]
      : "MMMM DD, YYYY";
  if (formatType === "time") {
    let Operation = restParams[2];
    // Accept "HH:mm", "HHmm", and Date/ISO values used across event timings.
    let Time = Moment(
      reqDate,
      ["HH:mm", "H:mm", "hh:mm A", "hhmm", "HHmm", Moment.ISO_8601],
      true
    );
    if (!Time.isValid()) {
      Time = Moment(reqDate);
    }
    if (!Time.isValid()) {
      return "";
    }
    if (Operation) {
      Operation == "Add" ? Time.add(reqDate) : Time.subtract(reqDate);
    }

    return Time.format(format || "h:mm A");
  }
  const date = Moment.utc(reqDate);
  if (!date.isValid()) {
    return "";
  }
  return date.format(format);
}

function getCurrentDate(givenDate = "") {
  return givenDate ? Moment(givenDate) : Moment();
}

function normalizeMediaUrl(url = "") {
  return String(url)
    .replace(/com\/\//g, "com/")
    .replace(/danzflomusic\.ast-1/gi, "danzflomusic.s3.us-east-1")
    .replace(
      /danzflomusic2\.s3\.us-east-1\.amazonaws\.com/gi,
      "danzflomusic2.s3.us-east-2.amazonaws.com"
    );
}

function extractAbsoluteMediaUrl(url = "") {
  const trimmed = String(url).trim();
  if (!trimmed) {
    return "";
  }

  const httpsIndex = trimmed.lastIndexOf("https://");
  const httpIndex = trimmed.lastIndexOf("http://");
  const protocolIndex = Math.max(httpsIndex, httpIndex);

  if (protocolIndex >= 0) {
    return trimmed.slice(protocolIndex).split(/[\s"'<>]/)[0];
  }

  return trimmed;
}

function getEventPublicBase() {
  const fromEnv = String(process.env.REACT_APP_BASE_URL || "").replace(/\/+$/, "");
  if (fromEnv && !fromEnv.includes("undefined")) {
    return fromEnv;
  }
  if (typeof window !== "undefined") {
    const pub = String(process.env.PUBLIC_URL || "/event").replace(/\/+$/, "");
    const path = pub.startsWith("/") ? pub : `/${pub}`;
    return `${window.location.origin}${path === "/" ? "" : path}`;
  }
  return "/event";
}

/** Static files live under /event/assets when the app is hosted on a subpath. */
function fixEventStaticAssetUrl(url = "") {
  const absolute = extractAbsoluteMediaUrl(url);
  if (!/^https?:\/\//i.test(absolute)) {
    return absolute;
  }

  const publicSegment = String(process.env.PUBLIC_URL || "/event").replace(
    /\/+$/,
    ""
  );
  if (!publicSegment || publicSegment === "/") {
    return absolute;
  }

  try {
    const parsed = new URL(absolute);
    if (
      parsed.pathname.startsWith("/assets/") &&
      !parsed.pathname.startsWith(`${publicSegment}/`)
    ) {
      parsed.pathname = `${publicSegment}${parsed.pathname}`;
      return parsed.toString();
    }
  } catch (_) {
    // keep original
  }

  return absolute;
}

function getEventImageRecord(mainimage) {
  if (!mainimage) return null;
  if (Array.isArray(mainimage)) {
    let entry = mainimage.length ? mainimage[0] : null;
    if (Array.isArray(entry)) {
      entry = entry[0];
    }
    return entry || null;
  }
  if (typeof mainimage === "string") {
    return { path: mainimage };
  }
  if (typeof mainimage === "object") {
    return mainimage.path ||
      mainimage.filename ||
      mainimage.Location ||
      mainimage.url
      ? mainimage
      : null;
  }
  return null;
}

function getEventImageUrl(mainimage, fallback = "") {
  return computeEventImageUrl(mainimage, fallback);
}

function computeEventImageUrl(mainimage, fallback = "") {
  const defaultFallback =
    fallback ||
    (process.env.REACT_APP_BASE_URL
      ? `${process.env.REACT_APP_BASE_URL}/assets/img/sellingevents.png`
      : "/event/assets/img/sellingevents.png");
  const record = getEventImageRecord(mainimage);
  if (!record) {
    return defaultFallback;
  }

  if (typeof record === "string") {
    return getImageUrl(record) || defaultFallback;
  }

  const directPath =
    record.path || record.Location || record.url || record.secure_url || "";
  if (directPath) {
    return getImageUrl(directPath) || defaultFallback;
  }

  const filename = String(record.filename || "").replace(/^\//, "");
  if (!filename) {
    return defaultFallback;
  }

  if (/^https?:\/\//i.test(filename)) {
    return getImageUrl(filename) || defaultFallback;
  }

  const destination = String(record.destination || "").trim().replace(/\/$/, "");
  if (/^https?:\/\//i.test(destination)) {
    return getImageUrl(`${destination}/${filename}`) || defaultFallback;
  }

  const storage = record.imageStorage || process.env.REACT_APP_IMAGESTORAGE;
  if (storage === "s3") {
    const s3Base = String(process.env.REACT_APP_Img_BASE_URL_S3 || "");
    return getImageUrl(`${s3Base}${filename}`) || defaultFallback;
  }

  const apiBase = String(process.env.REACT_APP_API_BASE_URL || "");
  return (
    getImageUrl(`${destination ? `${destination}/` : ""}${filename}`) ||
    getImageUrl(`${apiBase}${filename}`) ||
    defaultFallback
  );
}

/** Prefer mainimage, then first gallery image. */
function getEventPosterUrl(eventOrMainimage, fallback = "", gallery) {
  const defaultFallback =
    fallback ||
    (process.env.REACT_APP_BASE_URL
      ? `${process.env.REACT_APP_BASE_URL}/assets/img/sellingevents.png`
      : "/event/assets/img/sellingevents.png");

  let mainimage = eventOrMainimage;
  let galleryImages = gallery;
  if (
    eventOrMainimage &&
    typeof eventOrMainimage === "object" &&
    !Array.isArray(eventOrMainimage) &&
    (Object.prototype.hasOwnProperty.call(eventOrMainimage, "mainimage") ||
      Object.prototype.hasOwnProperty.call(eventOrMainimage, "gallery"))
  ) {
    mainimage = eventOrMainimage.mainimage;
    galleryImages =
      gallery !== undefined ? gallery : eventOrMainimage.gallery;
  }

  const resolveWithoutFallback = (source) => {
    const record = getEventImageRecord(source);
    if (!record) {
      return "";
    }
    if (typeof record === "string") {
      return getImageUrl(record) || "";
    }
    const directPath =
      record.path || record.Location || record.url || record.secure_url || "";
    if (directPath) {
      return getImageUrl(directPath) || "";
    }
    const filename = String(record.filename || "").replace(/^\//, "");
    if (!filename) {
      return "";
    }
    if (/^https?:\/\//i.test(filename)) {
      return getImageUrl(filename) || "";
    }
    const destination = String(record.destination || "")
      .trim()
      .replace(/\/$/, "");
    if (/^https?:\/\//i.test(destination)) {
      return getImageUrl(`${destination}/${filename}`) || "";
    }
    const storage = record.imageStorage || process.env.REACT_APP_IMAGESTORAGE;
    if (storage === "s3") {
      const s3Base = String(process.env.REACT_APP_Img_BASE_URL_S3 || "");
      return getImageUrl(`${s3Base}${filename}`) || "";
    }
    const apiBase = String(process.env.REACT_APP_API_BASE_URL || "");
    return (
      getImageUrl(`${destination ? `${destination}/` : ""}${filename}`) ||
      getImageUrl(`${apiBase}${filename}`) ||
      ""
    );
  };

  return (
    resolveWithoutFallback(mainimage) ||
    resolveWithoutFallback(galleryImages) ||
    defaultFallback
  );
}

function getAttendeePhotoUrl(photo, fallback = "") {
  const defaultFallback =
    fallback || "/event/assets/img/user.png";
  if (!photo) {
    return defaultFallback;
  }

  let entry = Array.isArray(photo) ? photo[0] : photo;
  // Some payloads nest the upload record one level deeper
  if (Array.isArray(entry)) {
    entry = entry[0];
  }
  if (!entry) {
    return defaultFallback;
  }

  if (typeof entry === "string") {
    return getImageUrl(entry) || defaultFallback;
  }

  if (typeof entry !== "object") {
    return defaultFallback;
  }

  const directPath =
    entry.path || entry.Location || entry.url || entry.secure_url || "";
  if (directPath) {
    return getImageUrl(directPath) || defaultFallback;
  }

  const filename = String(entry.filename || "").replace(/^\//, "");
  if (!filename) {
    return defaultFallback;
  }

  if (/^https?:\/\//i.test(filename)) {
    return getImageUrl(filename) || defaultFallback;
  }

  const destination = String(entry.destination || "").trim().replace(/\/$/, "");
  if (/^https?:\/\//i.test(destination)) {
    return getImageUrl(`${destination}/${filename}`) || defaultFallback;
  }

  const storage = entry.imageStorage || process.env.REACT_APP_IMAGESTORAGE;
  if (storage === "s3") {
    const s3Base = String(process.env.REACT_APP_Img_BASE_URL_S3 || "");
    return getImageUrl(`${s3Base}${filename}`) || defaultFallback;
  }

  const apiBase = String(process.env.REACT_APP_API_BASE_URL || "");
  return (
    getImageUrl(`${destination ? `${destination}/` : ""}${filename}`) ||
    getImageUrl(`${apiBase}${filename}`) ||
    defaultFallback
  );
}

function isDirectS3Url(url = "") {
  try {
    const parsed = new URL(url);
    if (parsed.pathname.includes("/media/proxy")) {
      return false;
    }
    const host = parsed.hostname.toLowerCase();
    return host.endsWith("amazonaws.com") || host.includes(".s3.");
  } catch (error) {
    return false;
  }
}

/** Bucket objects are private. Public S3 GETs 403, so <img> must use the API proxy. */
function proxyPrivateMediaUrl(url = "") {
  if (!isDirectS3Url(url)) {
    return url;
  }
  const base = String(getApiBaseUrl() || "").replace(/\/?$/, "/");
  if (!base || base.includes("undefined")) {
    return url;
  }
  return `${base}media/proxy?url=${encodeURIComponent(url)}`;
}

function getImageUrl(givenUrl = "") {
  const fallback = DEFAULT_PROFILE_IMAGE;
  if (!givenUrl) {
    return fallback;
  }

  const absolute = extractAbsoluteMediaUrl(givenUrl);
  if (!absolute) {
    return fallback;
  }

  if (/^https?:\/\//i.test(absolute)) {
    return proxyPrivateMediaUrl(normalizeMediaUrl(fixEventStaticAssetUrl(absolute)));
  }

  const relativePath = absolute.replace(/^\//, "");
  const baseUrl =
    process.env.REACT_APP_IMAGESTORAGE == "s3"
      ? `${process.env.REACT_APP_Img_BASE_URL_S3}${relativePath}`
      : `${process.env.REACT_APP_API_BASE_URL}${relativePath}`;

  return proxyPrivateMediaUrl(normalizeMediaUrl(baseUrl));
}

function createObjectForPaginatedResult(
  currentRes,
  prevRes,
  paginateType = "scroll"
) {
  console.log({ currentRes, prevRes });
  if (
    process.env.REACT_APP_PAGINATION_TYPE == "scroll" &&
    paginateType != "paginate"
  ) {
    let itemData = [
      ...prevRes?.data?.itemsList,
      ...currentRes?.data?.itemsList,
    ];
    return {
      status: currentRes.status,
      msg: currentRes.msg,
      data: { itemsList: itemData, paginator: currentRes?.data?.paginator },
    };
  }
  return currentRes;
}

function dataURLtoFile(dataurl, filename) {
  var arr = dataurl.split(","),
    mime = arr[0].match(/:(.*?);/)[1],
    bstr = atob(arr[1]),
    n = bstr.length,
    u8arr = new Uint8Array(n);

  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }

  return new File([u8arr], filename, { type: mime });
}

function getFormData(o, ImageField = []) {
  console.log(o);
  const formData = new FormData();
  for (const [key, value] of Object.entries(o)) {
    if (ImageField.includes(key)) {
      value.forEach((file) => {
        formData.append(key, file);
      });
    } else {
      formData.append(key, value);
    }
  }
  return formData;
}

const AddSubTractDate = (givenDate, valueForAddSub) => {
  var newdate = new Date(givenDate);
  newdate.setDate(newdate.getDate() - valueForAddSub);
  return newdate;
};

const trimString = (string, size = 15) => {
  let trimmedString = string?.slice(0, size);
  if (string?.length > size) {
    trimmedString += "...";
  }
  return trimmedString;
};

const sanitizePdfFileName = (title = "ticket") => {
  const cleaned = String(title || "ticket")
    .replace(/[^\w\s-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  return cleaned || "ticket";
};

const PDF_FALLBACK_IMAGE = "/event/assets/img/user.png";
const PDF_EVENT_FALLBACK_IMAGE = "/event/assets/img/sellingevents.png";
// Neutral placeholders that do not depend on (misnamed) brand assets like user-img-1.png.
const PDF_AVATAR_DATA_URL =
  "data:image/svg+xml," +
  encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" width="256" height="256" viewBox="0 0 256 256">
      <rect width="256" height="256" fill="#1a2332"/>
      <circle cx="128" cy="96" r="52" fill="#5b6b7c"/>
      <path d="M32 232c12-56 56-88 96-88s84 32 96 88" fill="#5b6b7c"/>
    </svg>`
  );
const PDF_EVENT_DATA_URL =
  "data:image/svg+xml," +
  encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" width="640" height="400" viewBox="0 0 640 400">
      <defs>
        <linearGradient id="g" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stop-color="#152033"/>
          <stop offset="100%" stop-color="#0b1220"/>
        </linearGradient>
      </defs>
      <rect width="640" height="400" fill="url(#g)"/>
      <rect x="48" y="48" width="544" height="304" rx="16" fill="#243247"/>
      <circle cx="320" cy="180" r="46" fill="#3d4f66"/>
      <rect x="220" y="250" width="200" height="18" rx="9" fill="#3d4f66"/>
    </svg>`
  );
const IMAGE_EXT_RE = /\.(avif|bmp|gif|jpe?g|png|svg|webp)(?:$|\?)/i;

const getMediaProxyCandidates = (src = "") => {
  const absolute = extractAbsoluteMediaUrl(src);
  if (!absolute || !/^https?:\/\//i.test(absolute)) {
    return [];
  }

  const bases = [];
  const configured = getApiBaseUrl();
  if (configured && !configured.includes("undefined")) {
    bases.push(configured);
  }

  // Live hosts proxy /event/api/v2 via nginx. CRA localhost does not —
  // skipping that candidate avoids long failed fetches on download.
  if (
    typeof window !== "undefined" &&
    isDanzfloSiteHost(window.location.hostname)
  ) {
    const originBase = `${window.location.origin}/event/api/v2/`;
    if (!bases.includes(originBase)) {
      bases.push(originBase);
    }
  }

  return bases.map(
    (base) => `${base}media/proxy?url=${encodeURIComponent(absolute)}`
  );
};

const getMediaProxyUrl = (src = "") => getMediaProxyCandidates(src)[0] || "";

const isLikelyImageBlob = (blob, src = "") => {
  if (!blob) {
    return false;
  }
  const type = String(blob.type || "")
    .toLowerCase()
    .split(";")[0]
    .trim();
  if (type.startsWith("image/")) {
    return true;
  }
  // S3 uploads are often served as octet-stream / empty type.
  if (!type || type === "application/octet-stream" || type === "binary/octet-stream") {
    return IMAGE_EXT_RE.test(src) || blob.size > 0;
  }
  return false;
};

const loadedImageToDataUrl = (imgEl) => {
  if (!imgEl || !imgEl.complete || !imgEl.naturalWidth || imgEl.naturalWidth < 2) {
    return null;
  }
  try {
    const canvas = document.createElement("canvas");
    canvas.width = imgEl.naturalWidth;
    canvas.height = imgEl.naturalHeight;
    const ctx = canvas.getContext("2d");
    ctx.fillStyle = "#0b1220";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(imgEl, 0, 0);
    return canvas.toDataURL("image/jpeg", 0.92);
  } catch (error) {
    return null;
  }
};

const blobToDataUrl = (blob) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });

const fetchImageAsDataUrl = async (src, options = {}) => {
  const response = await fetch(src, options);
  if (!response.ok) {
    throw new Error("Image fetch failed");
  }
  const blob = await response.blob();
  if (!isLikelyImageBlob(blob, src)) {
    return null;
  }
  return blobToDataUrl(blob);
};

const loadImageElementAsDataUrl = (src) =>
  new Promise((resolve) => {
    if (!src) {
      resolve(null);
      return;
    }
    const img = new Image();
    img.crossOrigin = "anonymous";
    const timer = setTimeout(() => resolve(null), 12000);
    img.onload = () => {
      clearTimeout(timer);
      resolve(loadedImageToDataUrl(img));
    };
    img.onerror = () => {
      clearTimeout(timer);
      resolve(null);
    };
    img.src = src;
  });

const imageToDataUrl = async (src) => {
  if (!src) {
    return null;
  }
  if (src.startsWith("data:image/")) {
    return src;
  }
  if (src.startsWith("data:") && !src.startsWith("data:image/")) {
    return null;
  }

  const absolute = extractAbsoluteMediaUrl(src) || src;
  const isRemote = /^https?:\/\//i.test(absolute);
  const sameOrigin =
    !isRemote ||
    (typeof window !== "undefined" &&
      absolute.startsWith(window.location.origin));

  // Cross-origin S3 objects have no CORS headers — hit the API proxy first.
  if (isRemote && !sameOrigin) {
    const proxyCandidates = getMediaProxyCandidates(absolute);
    for (const proxyUrl of proxyCandidates) {
      try {
        const dataUrl = await fetchImageAsDataUrl(proxyUrl, {
          mode: "cors",
          credentials: "omit",
          cache: "no-cache",
        });
        if (dataUrl) {
          return dataUrl;
        }
      } catch (proxyError) {
        // try next candidate
      }

      const proxiedCanvas = await loadImageElementAsDataUrl(proxyUrl);
      if (proxiedCanvas) {
        return proxiedCanvas;
      }
    }
  }

  try {
    const dataUrl = await fetchImageAsDataUrl(absolute, {
      mode: sameOrigin ? "same-origin" : "cors",
      credentials: "omit",
      cache: "force-cache",
    });
    if (dataUrl) {
      return dataUrl;
    }
  } catch (error) {
    // continue to fallbacks
  }

  // Same-origin relative assets often work without CORS mode
  if (!/^https?:\/\//i.test(src)) {
    try {
      const dataUrl = await fetchImageAsDataUrl(src, { cache: "force-cache" });
      if (dataUrl) {
        return dataUrl;
      }
    } catch (innerError) {
      // continue
    }
  }

  return loadImageElementAsDataUrl(
    isRemote && !sameOrigin
      ? getMediaProxyCandidates(absolute)[0] || absolute
      : absolute
  );
};

const findSourceImageForClone = (cloneImg, sourceImgs, index) => {
  const cloneSrc =
    cloneImg?.currentSrc || cloneImg?.getAttribute("src") || "";
  const cloneClass = cloneImg?.className || "";

  if (cloneClass) {
    const byClass = sourceImgs.find((img) => img?.className === cloneClass);
    if (byClass) {
      return byClass;
    }
  }

  if (cloneSrc) {
    const bySrc = sourceImgs.find((img) => {
      const src = img?.currentSrc || img?.getAttribute("src") || "";
      return src && src === cloneSrc;
    });
    if (bySrc) {
      return bySrc;
    }
  }

  return sourceImgs[index] || null;
};

const getFallbackForImage = (img) => {
  const role = img?.getAttribute?.("data-pdf-role") || "";
  const className = String(
    img?.getAttribute?.("class") || img?.className || ""
  );
  if (
    role === "event" ||
    role === "gallery" ||
    className.includes("event-order-detail-image")
  ) {
    return { path: PDF_EVENT_FALLBACK_IMAGE, dataUrl: PDF_EVENT_DATA_URL };
  }
  return { path: PDF_FALLBACK_IMAGE, dataUrl: PDF_AVATAR_DATA_URL };
};

const inlineImagesForPdf = async (clone, sourceRoot) => {
  const sourceImgs = Array.from(sourceRoot.querySelectorAll("img"));
  const cloneImgs = Array.from(clone.querySelectorAll("img"));
  const fallbackCache = new Map();

  const resolveFallbackDataUrl = async (fallback) => {
    const key = fallback.path;
    if (fallbackCache.has(key)) {
      return fallbackCache.get(key);
    }
    const absoluteFallback = `${window.location.origin}${fallback.path}`;
    const dataUrl =
      (await imageToDataUrl(absoluteFallback)) ||
      (await imageToDataUrl(fallback.path)) ||
      fallback.dataUrl;
    fallbackCache.set(key, dataUrl);
    return dataUrl;
  };

  await Promise.all(
    cloneImgs.map(async (img, index) => {
      const sourceImg = findSourceImageForClone(img, sourceImgs, index);
      const role =
        sourceImg?.getAttribute?.("data-pdf-role") ||
        img?.getAttribute?.("data-pdf-role") ||
        "";
      const src =
        sourceImg?.currentSrc ||
        sourceImg?.getAttribute("src") ||
        img.getAttribute("src") ||
        "";

      // Prefer proxy/fetch for remote event/profile images — on-screen <img>
      // from S3 is usually CORS-tainted and canvas export fails silently.
      let dataUrl = null;
      const isRemote =
        /^https?:\/\//i.test(src) &&
        (typeof window === "undefined" ||
          !src.startsWith(window.location.origin));

      if (role === "event" || role === "profile" || role === "gallery" || isRemote) {
        dataUrl = await imageToDataUrl(src);
      }

      if (!dataUrl) {
        dataUrl = loadedImageToDataUrl(sourceImg);
      }

      if (!dataUrl) {
        dataUrl = await imageToDataUrl(src);
      }

      const fallback = getFallbackForImage(img);
      const finalSrc =
        dataUrl || (await resolveFallbackDataUrl(fallback)) || fallback.dataUrl;

      img.removeAttribute("srcset");
      img.removeAttribute("crossorigin");
      img.setAttribute("src", finalSrc);
      img.style.objectFit = "cover";
      img.style.display = "block";
      img.style.maxWidth = "100%";

      // Layout images must fill their PDF columns — locking on-screen pixel
      // widths makes the event thumbnail narrower than the details card.
      if (role === "event" || role === "gallery") {
        img.style.width = "100%";
        img.style.height = "100%";
        img.style.minWidth = "0";
        img.style.minHeight = role === "event" ? "220px" : "90px";
      } else if (role === "profile") {
        const rect = sourceImg?.getBoundingClientRect?.();
        const size = Math.max(
          Math.round(Math.min(rect?.width || 0, rect?.height || 0) || 96),
          72
        );
        img.style.width = `${size}px`;
        img.style.height = `${size}px`;
        img.style.borderRadius = "50%";
      } else {
        const rect = sourceImg?.getBoundingClientRect?.();
        const width = Math.max(Math.round(rect?.width || img.width || 0), 1);
        const height = Math.max(Math.round(rect?.height || img.height || 0), 1);
        img.style.width = `${width}px`;
        img.style.height = `${height}px`;
      }
    })
  );

  // Wait a tick so browsers decode replaced data URLs before capture
  await Promise.all(
    cloneImgs.map(
      (img) =>
        new Promise((resolve) => {
          if (img.complete && img.naturalWidth > 0) {
            resolve();
            return;
          }
          img.onload = () => resolve();
          img.onerror = () => resolve();
          setTimeout(resolve, 800);
        })
    )
  );
};

const rasterizeSvgsForPdf = async (root) => {
  const svgs = Array.from(root.querySelectorAll("svg"));
  await Promise.all(
    svgs.map(async (svg) => {
      try {
        const xml = new XMLSerializer().serializeToString(svg);
        const url = URL.createObjectURL(
          new Blob([xml], { type: "image/svg+xml;charset=utf-8" })
        );
        const image = await new Promise((resolve, reject) => {
          const img = new Image();
          img.onload = () => resolve(img);
          img.onerror = reject;
          img.src = url;
        });
        const bbox = svg.getBoundingClientRect();
        const width = Math.max(
          image.width || Math.round(bbox.width) || 148,
          1
        );
        const height = Math.max(
          image.height || Math.round(bbox.height) || 148,
          1
        );
        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext("2d");
        ctx.fillStyle = "#ffffff";
        ctx.fillRect(0, 0, width, height);
        ctx.drawImage(image, 0, 0, width, height);
        URL.revokeObjectURL(url);

        const replacement = document.createElement("img");
        replacement.src = canvas.toDataURL("image/png");
        replacement.width = width;
        replacement.height = height;
        replacement.alt = "QR code";
        replacement.style.cssText =
          svg.getAttribute("style") ||
          "width:148px;height:148px;display:block;";
        svg.replaceWith(replacement);
      } catch (error) {
        // Keep original SVG if rasterization fails
      }
    })
  );
};

const downloadPDF = async (title = "ticket") => {
  const element = document.querySelector(".divToPrint");
  if (!element) {
    throw new Error("Ticket content not found");
  }

  const clone = element.cloneNode(true);
  clone.classList.add("is-pdf-capture");
  clone.querySelectorAll(".no-print, .modal, .modal-backdrop").forEach((node) => {
    node.remove();
  });
  clone.style.cssText = [
    "position:fixed",
    "left:-10000px",
    "top:0",
    "width:760px",
    "background:#0b1220",
    "padding:28px",
    "border-radius:16px",
    "box-sizing:border-box",
    "z-index:-1",
    "pointer-events:none",
  ].join(";");
  document.body.appendChild(clone);

  try {
    await inlineImagesForPdf(clone, element);
    await rasterizeSvgsForPdf(clone);

    const canvas = await html2canvas(clone, {
      useCORS: true,
      allowTaint: false,
      scale: 2,
      backgroundColor: "#0b1220",
      logging: false,
      imageTimeout: 15000,
    });

    const pdf = new jsPDF({
      orientation: "portrait",
      unit: "pt",
      format: "a4",
    });

    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();

    pdf.setFillColor(11, 18, 32);
    pdf.rect(0, 0, pageWidth, pageHeight, "F");

    const marginX = 36;
    const marginTop = 40;
    const maxWidth = pageWidth - marginX * 2;
    const maxHeight = pageHeight - marginTop - 40;
    const ratio = Math.min(maxWidth / canvas.width, maxHeight / canvas.height);
    const imgWidth = canvas.width * ratio;
    const imgHeight = canvas.height * ratio;
    const offsetX = (pageWidth - imgWidth) / 2;
    const offsetY = marginTop;

    pdf.addImage(
      canvas.toDataURL("image/jpeg", 0.95),
      "JPEG",
      offsetX,
      offsetY,
      imgWidth,
      imgHeight
    );
    pdf.save(`${sanitizePdfFileName(title)}.pdf`);
  } finally {
    clone.remove();
  }
};

const readAxiosBlobError = async (error) => {
  const data = error?.response?.data;
  if (!data) {
    return error?.message || "";
  }
  try {
    if (typeof data === "string") {
      const payload = JSON.parse(data);
      return payload?.message || data;
    }
    if (typeof Blob !== "undefined" && data instanceof Blob) {
      const text = await data.text();
      try {
        const payload = JSON.parse(text || "{}");
        return payload?.message || text;
      } catch (parseError) {
        return text;
      }
    }
    if (typeof data?.message === "string") {
      return data.message;
    }
  } catch (readError) {
    // fall through
  }
  return error?.message || "";
};

const downloadTicketPdfFile = async ({
  orderId,
  attendeeId,
  fileName = "ticket",
}) => {
  const userData = getUserData();
  const safeName = sanitizePdfFileName(fileName);

  const tryServerDownload = async () => {
    if (!orderId || !attendeeId || !userData?.token) {
      throw new Error("Missing order details for server download");
    }

    const response = await axios.post(
      `${process.env.REACT_APP_API_BASE_URL}orders/ticket/download`,
      { orderId, attendeeId },
      {
        responseType: "blob",
        // Server regenerates PDF with embedded images; allow enough time.
        timeout: 55000,
        headers: {
          Authorization: `Bearer ${userData.token}`,
          "Content-Type": "application/json",
        },
      }
    );

    const contentType = response.headers?.["content-type"] || "";
    if (
      contentType.includes("application/json") ||
      contentType.includes("text/html")
    ) {
      const text = await response.data.text();
      let message = "Unable to download ticket PDF";
      try {
        const payload = JSON.parse(text || "{}");
        message = payload?.message || message;
      } catch (parseError) {
        if (text?.includes("504")) {
          message = "Ticket download timed out";
        }
      }
      throw new Error(message);
    }

    const blobUrl = window.URL.createObjectURL(response.data);
    const link = document.createElement("a");
    link.href = blobUrl;
    link.download = `${safeName}.pdf`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(blobUrl);
    return "downloaded";
  };

  // Always try on-screen capture first (ticket page already has images via
  // media/proxy). Fall back to server PDF if capture fails.
  try {
    await Promise.race([
      downloadPDF(safeName),
      new Promise((_, reject) =>
        setTimeout(
          () => reject(new Error("On-screen ticket capture timed out")),
          25000
        )
      ),
    ]);
    return "generated";
  } catch (clientError) {
    try {
      return await tryServerDownload();
    } catch (error) {
      const apiMessage = await readAxiosBlobError(error);
      throw new Error(
        apiMessage ||
          clientError?.message ||
          "Unable to download ticket. Please try again."
      );
    }
  }
};

const printDocument = () => {
  var content = document.querySelector(".divToPrint");
  var iframe = document.querySelector("#ifmcontentstoprint");
  const body = iframe.contentDocument.body;
  body.appendChild(content);
  iframe.contentWindow.print();
};

const timeSince = (date) => {
  return moment(date).fromNow();
};

const checkImageExists = (url) => {
  return new Promise((resolve, reject) => {
    axios
      .get(url, {
        responseType: "arraybuffer",
      })
      .then((res) => {
        resolve(true);
      })
      .catch((err) => {
        resolve(false);
      });
  });
};

const convertToTimeZone = (time, zone) => {
  if (!time) return "";
  try {
    const m = momentTimezone(time);
    if (!m.isValid()) return "";
    if (zone && momentTimezone.tz.zone(zone)) {
      return m.tz(zone).format("z");
    }
    return m.format("z");
  } catch (error) {
    return "";
  }
};

const combineDateAndTime = (date, hour, minute) => {
  const combinedDateTime = new Date(date);
  combinedDateTime.setHours(hour);
  combinedDateTime.setMinutes(minute);
  return combinedDateTime;
};

const formatHourMinute = (dateTime) => {
  const date = moment(dateTime).format("YYYY-MM-DD HH:mm");
  return date;
};
const checkEventValidityWithTicket = (
  ticketType,
  eventEndDate,
  eventStartDate
) => {
  let ticketName;
  let msg;
  const checkEventValidity = ticketType?.some((ticket) => {
    const eventEndDateTime = formatHourMinute(eventEndDate);
    const eventStartDateTime = formatHourMinute(eventStartDate);
    const salesEndDateTime = formatHourMinute(ticket?.salesEnd);

    if (eventEndDateTime < salesEndDateTime) {
      ticketName = ticket?.name;
      msg = `The ticket ${ticketName} sale should end before the event end date.`;
      return true;
    } else {
      const isTicketValidity = ticket?.ticketEnd?.some((ticket) => {
        const ticketEndTime = formatHourMinute(ticket);
        return eventEndDateTime < ticketEndTime;
      });
      const isTicketStartValidity = ticket?.ticketStart?.some((ticket) => {
        const ticktStartTime = formatHourMinute(ticket);
        return eventStartDateTime > ticktStartTime;
      });
      if (isTicketValidity) {
        ticketName = ticket?.name;
        msg = `The ticket ${ticketName} validity should end before the event end date.`;
        return true;
      } else if (isTicketStartValidity) {
        ticketName = ticket?.name;
        msg = `The ticket ${ticketName} start validity should be greater than event start date.`;
        return true;
      } else {
        return false;
      }
    }
  });
  return { checkEventValidity, ticketName, msg };
};
const formatDateInputValidation = (dateString) => {
  const date = new Date(dateString);
  const year = date.getUTCFullYear();
  const month = String(date.getUTCMonth() + 1).padStart(2, "0");
  const day = String(date.getUTCDate()).padStart(2, "0");
  const formattedDate = `${year}-${month}-${day}T23:59`;
  return formattedDate;
};
const formatDateInputValidationWithMin = (dateString) => {
  const date = new Date(dateString);
  const year = date.getUTCFullYear();
  const month = String(date.getUTCMonth() + 1).padStart(2, "0");
  const day = String(date.getUTCDate()).padStart(2, "0");
  const formattedDate = `${year}-${month}-${day}T00:00`;
  return formattedDate;
};
const combineDate = (dateString, hour, minute) => {
  const date = new Date(dateString);
  const year = date.getUTCFullYear();
  const month = String(date.getUTCMonth() + 1).padStart(2, "0");
  const day = String(date.getUTCDate()).padStart(2, "0");
  const formattedDate = `${year}-${month}-${day}T${hour ?? "00"}:${
    minute ?? "00"
  }`;
  return formattedDate;
};
const combineDateTime = (dateString, hour, minute) => {
  const date = new Date(dateString);
  date.setUTCHours(hour);
  date.setUTCMinutes(minute);
  return date;
};
const getPaidOrRemaining = (details) => {
  const total = Number(details?.total || 0);
  const totalInstallment = Number(details?.emiInfo?.installment || 0);
  const paidCount = Number(details?.emiInfo?.paid || 0);
  const remainingCount = Number(
    details?.emiInfo?.remaining ?? Math.max(0, totalInstallment - paidCount)
  );

  if (!totalInstallment || !Number.isFinite(total)) {
    return {
      paid: "0.00",
      remaining: Number.isFinite(total) ? total.toFixed(2) : "0.00",
    };
  }

  const monthlyPrice = total / totalInstallment;
  const paidAmount = paidCount * monthlyPrice;
  const remainingAmount = remainingCount * monthlyPrice;

  return {
    paid: Number.isFinite(paidAmount) ? paidAmount.toFixed(2) : "0.00",
    remaining: Number.isFinite(remainingAmount)
      ? remainingAmount.toFixed(2)
      : "0.00",
  };
};
export {
  combineDate,
  combineDateTime,
  combineDateAndTime,
  formatHourMinute,
  checkEventValidityWithTicket,
  formatDateInputValidation,
  formatDateInputValidationWithMin,
  getGuardData,
  readQueryLoginToken,
  buildQueryTokenLoginPath,
  beginPlatformLogout,
  isPlatformLogout,
  platformLogoutUrl,
  redirectUnauthorized,
  timeSince,
  getUserData,
  dateFormatter,
  getImageUrl,
  getEventImageUrl,
  getEventPosterUrl,
  getAttendeePhotoUrl,
  getCurrentDate,
  createObjectForPaginatedResult,
  dataURLtoFile,
  getFormData,
  AddSubTractDate,
  trimString,
  downloadPDF,
  downloadTicketPdfFile,
  printDocument,
  checkImageExists,
  convertToTimeZone,
  getPaidOrRemaining,
  DEFAULT_PROFILE_IMAGE,
  DEFAULT_BANNER_IMAGE,
};
